import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSearchSuggestions } from '../../hooks/useSearchSuggestions';

/**
 * Global Search Component
 * 
 * Global search dialog component with:
 * - Search input with Enter key support
 * - Navigation to search results
 * - Modern gradient design
 * - Backdrop blur effect
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @returns {JSX.Element} The global search component
 */
const GlobalSearch = ({ open, onClose }) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const { suggestions, loading: suggestionsLoading, setQuery: setSuggestionsQuery } = useSearchSuggestions('/api/search/suggestions', 2);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSearch = (event) => {
    if (event.key === 'Enter' && query.trim()) {
      // Determine the correct search path based on current location
      let searchPath = '/client/search'; // Default to client
      const currentPath = location.pathname;
      
      if (currentPath.startsWith('/admin')) {
        searchPath = '/admin/search';
      } else if (currentPath.startsWith('/manager')) {
        searchPath = '/manager/search';
      } else if (currentPath.startsWith('/operator')) {
        searchPath = '/operator/search';
      } else if (currentPath.startsWith('/client')) {
        searchPath = '/client/search';
      }
      
      navigate(`${searchPath}?q=${encodeURIComponent(query.trim())}`);
      onClose();
      setQuery('');
    }
  };

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <TextField
          fullWidth
          label={t('search.global', 'Global Search')}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSuggestionsQuery(e.target.value); }}
          onKeyDown={handleSearch}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'primary.main' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClose} size="small">
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              fontSize: '1.1rem',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                }
              }
            }
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '1.1rem',
              py: 1.5
            }
          }}
        />
        
        {suggestionsLoading && query.length >= 2 && (
          <Paper sx={{ mt: 1, p: 1, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Paper>
        )}
        {suggestions?.length > 0 && (
          <Paper sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
            <List dense>
              {suggestions.map((item, idx) => (
                <ListItem key={idx} disablePadding>
                  <ListItemButton onClick={() => { setQuery(item.text || item.label || String(item)); navigate(`${location.pathname.startsWith('/admin') ? '/admin' : '/client'}/search?q=${encodeURIComponent(item.text || item.label || String(item))}`); handleClose(); }}>
                    <ListItemText primary={item.text || item.label || String(item)} secondary={item.category || null} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
        {query.length >= 2 && !suggestions?.length && !suggestionsLoading && (
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            backgroundColor: 'rgba(25, 118, 210, 0.1)', 
            borderRadius: '8px',
            textAlign: 'center',
            color: '#1976d2'
          }}>
            {t('search.pressEnter', 'Press Enter to search')} "{query}"
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(GlobalSearch);

